<template>
    <div class="loading-container">

        <!-- Animated Background Blobs -->
        <div class="blob blob1"></div>
        <div class="blob blob2"></div>
        <div class="blob blob3"></div>

        <!-- Content -->
        <div class="content">
            <div class="logo-wrapper">
                <!-- Rotating Ring -->
                <div class="ring"></div>

                <!-- Logo -->
                <img
                    src="@/assets/logo/new/idea3.png"
                    alt="SweldoNow"
                    class="logo"
                />
            </div>

            <h1 class="product-name">SweldoNow</h1>

            <div class="tagline" v-if="!isFirstVisit">
                The operating system for your workforce.
            </div>

            <div class="loading-text" v-if="isFirstVisit && !isInterruptedInstallation">
                <b>We are getting your workspace ready for your first visit!</b><br>
                This process may take a minute or two depending on your connection speed.<br v-if="!isMobile"> 
                To avoid losing progress, please do not close or refresh this window.
            </div>

            <div class="loading-text-dot" v-if="!isInterruptedInstallation">
                <span>{{ appStatusText }}</span>
                <span class="dots"></span>
            </div>

            <div v-if="isInterruptedInstallation" class="loading-text warning--text">
                <b>Setup is taking longer than expected</b>
                <br>

                Please keep this page open while
                SweldoNow finishes preparing your
                workspace.

                <br><br>

                If the issue persists, refresh
                the page and try again.
            </div>
        </div>
    </div>
</template>

<script>

import appsApi from '@/api/applications'

export default {
    name: 'SystemCheck',
    metaInfo: {
        title: 'System Check',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'System Check' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ],
    },
    data() {
        return {
            error: false,
            errorMessage: null,
            pwaState: 'checking',
            appStatusText: 'Preparing SweldoNow',
            isInterruptedInstallation: false,
            setupTimeout: null,
            isUpToDate: false
        };
    },
    methods: {
        startSetupTimer() {

            if (!this.isFirstVisit)
                return

            console.log('SW Safety Timer Started')

            this.setupTimeout = setTimeout(() => {

                if (
                    this.pwaState !== 'ready' &&
                    this.pwaState !== 'upToDate'
                ) {
                    console.warn(
                        'Installation taking longer than expected.'
                    )

                    this.isInterruptedInstallation = true
                }

            }, 90000) // 90 seconds
        },
        clearSetupTimer() {
            if (this.setupTimeout) {
                console.log('SW Safety Timer Cleared')
                clearTimeout(this.setupTimeout)
                this.setupTimeout = null
            }
        },
        async getLatestUIVersions() {
            console.log('getLatestUIVersions')
            try {
                this.pwaState = 'checking'
                const response = await appsApi.getUIVersions()

                if (!response || response.status !== 200) {
                    return
                }

                const latestVersion = (response.data.appUIVersion || '').toLowerCase().trim()
                const localVersion = (process.env.VUE_APP_UI_VERSION || '').toLowerCase().trim()

                this.isUpToDate = latestVersion === localVersion

                console.log('latestVersion: ', latestVersion)
                console.log('localVersion: ', localVersion)
                console.log('this.isUpToDate: ', this.isUpToDate)

                //if first time visit, getLatestUIVersions is invoked first before onPwaStateChange event is triggered 
                if (this.isUpToDate && !this.isFirstVisit) {
                    this.pwaState = 'upToDate'

                    this.clearSetupTimer() // Success: Clear timer
                    this.navigateToNextPage()

                    return
                }
            } catch (error) {
                this.error = true
                this.errorMessage = error
                this.clearSetupTimer() // Clear timer on API failure to prevent overlapping errors
            }
        },
        navigateToNextPage() {
            console.log('navigateToNextPage')

            // 1. Mark the navigation flag inside localStorage to clear the router guard
            localStorage.setItem('sweldonow_system_checked', 'true')
            
            // 2. Fetch any deep link location the user originally wanted to access
            const redirect = this.$route.query.redirect

            // 3. Safeguard: Clear the SW timeout
            this.clearSetupTimer() 

            // 4. Set has visited site 
            this.setVisitedFlag()
            
            setTimeout(() => {
                this.$router.replace({
                    path: '/signin-options',
                    query: redirect ? { redirect } : {}
                })
            }, 1000) // 1-second visual buffer to keep your animations visible
        },
        onPwaStateChange(event) {
            console.log('onPwaStateChange')
            this.pwaState = event.detail.state

            console.log('this.pwaState: ', this.pwaState)
            console.log('this.isFirstVisit: ', this.isFirstVisit)
            console.log('hasVisitedSweldoNow: ', localStorage.getItem('hasVisitedSweldoNow'))

            this.clearSetupTimer()

            if (this.pwaState === 'firstVisit') {
                this.appStatusText = 'Preparing your workspace'
            }

            if (this.pwaState === 'updateReady' || this.pwaState === 'downloading') {
                this.appStatusText = 'Loading the latest version'
            }

            if (this.pwaState === 'applying') {
                this.appStatusText = 'Finishing updates and refreshing'
            }

            if (this.pwaState === 'finalizing') {
                this.appStatusText = 'Finalizing workspace setup'
            }

            //this handles resumption of interrupted installation
            if (this.pwaState === 'ready' && this.isUpToDate) {
                this.appStatusText = 'Workspace ready'
                this.navigateToNextPage()
                return
            }
        },
        setVisitedFlag() {
            localStorage.setItem('hasVisitedSweldoNow', 'true')
            localStorage.setItem('sw_install_state', 'complete')
        }
    },
    async created() {
        if (this.installState === 'installing') {
            this.appStatusText = 'Resuming workspace setup'
        }

        if (process.env.NODE_ENV === 'development') {
            // localhost does not support PWA features, so we simulate the first visit state for testing purposes
             this.setVisitedFlag()
        }

        await this.getLatestUIVersions()
    },
    computed: {
        isFirstVisit() {
            // Check localStorage
            return !localStorage.getItem('hasVisitedSweldoNow')
        },
        installState() {
            return localStorage.getItem('sw_install_state')
        }
    },
    mounted() {
        window.addEventListener('pwa-state-change', this.onPwaStateChange)
        this.startSetupTimer() // Initialize safety countdown when the view renders
    },
    beforeDestroy() {
        window.removeEventListener('pwa-state-change', this.onPwaStateChange)
        this.clearSetupTimer() // Garbage collection to prevent memory leaks
    },
}
</script>

<style scoped>
/* CSS blocks stay completely identical to your clean animation configurations */
.loading-container {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
    background: #ffffff;
    display: flex;
    justify-content: center;
    align-items: center;
}
.content {
    text-align: center;
    z-index: 10;
}
.product-name {
    font-family: 'Inter', sans-serif !important;
    margin-top: 26px;
    font-size: 42px;
    font-weight: 500;
    color: #1976D2;
    animation: fadeUp .8s ease;
}
.tagline {
    font-family: 'Inter', sans-serif !important;
    font-size: 14px; /* Increased from 12px for better readability on small screens */
    font-weight: 500;
    color: #37474F; /* Explicit dark slate grey to pass WCAG contrast checks against background blobs */
   
    opacity: 0.85;
}
.logo-wrapper {
    position: relative;
    width: 140px;
    height: 140px;
    margin: auto;
    animation: float 3s ease-in-out infinite;
}
.logo {
    position: absolute;
    top: 20px;
    left: 20px;
    width: 100px;
    height: 100px;
    object-fit: contain;
    z-index: 2;
}
.ring {
    position: absolute;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    border: 2px solid rgba(25, 118, 210, .15);
    border-top: 2px solid #1976D2;
    animation: spin 3s linear infinite;
}
.blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    opacity: .45;
}
.blob1 {
    width: 350px;
    height: 350px;
    background: #90CAF9;
    top: -100px;
    left: -100px;
    animation: moveBlob1 10s ease-in-out infinite alternate;
}
.blob2 {
    width: 300px;
    height: 300px;
    background: #64B5F6;
    right: -100px;
    top: 20%;
    animation: moveBlob2 12s ease-in-out infinite alternate;
}
.blob3 {
    width: 400px;
    height: 400px;
    background: #BBDEFB;
    bottom: -150px;
    left: 35%;
    animation: moveBlob3 14s ease-in-out infinite alternate;
}
@keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-12px); }
}
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
@keyframes moveBlob1 {
    from { transform: translate(0, 0); }
    to { transform: translate(120px, 80px); }
}
@keyframes moveBlob2 {
    from { transform: translate(0, 0); }
    to { transform: translate(-120px, 100px); }
}
@keyframes moveBlob3 {
    from { transform: translate(0, 0); }
    to { transform: translate(100px, -100px); }
}
@keyframes fadeUp {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
}
.loading-text {
    margin-top: 32px;
    color: #607D8B;
    font-size: 16px;
    font-weight: 500;
    
    /* Crucial UX Fix: Changed min-height slightly to comfortably accommodate 
       multi-line text wraps internally without bouncing the layout container */
    min-height: 48px; 
    
    display: flex;
    flex-direction: column; /* 1. Forces items/lines to stack vertically */
    align-items: center;
    justify-content: center;
    text-align: center;     /* 2. Ensures lines of text are horizontally centered */
    padding: 0 24px; 
}
.loading-text-dot {
    color: #1976D2 !important;
    margin-top: 32px;
    font-size: 16px;
    font-weight: 500;
    
    /* Crucial UX Fix: Lock the height so multi-line text wraps 
       internally and never shifts the page layout or bounces the layout container */
    min-height: 44px; 
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 24px; /* Keeps text clear of edge boundaries on narrow phones */
}
.dots {
    display: inline-block;
    width: 24px;
    text-align: left;
}
.dots::after {
    content: '';
    animation: dots 1.5s infinite;
}
@keyframes dots {
    0%   { content: ''; }
    25%  { content: '.'; }
    50%  { content: '..'; }
    75%, 100% { content: '...'; }
}
</style>
